import axios from 'axios';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { generateSatelliteCertificationService } from '@siloreal:shared:api/service/silobag/satellite-certification-create';
import * as HOOKS from '@siloreal:shared:hooks';

type ResolveSilobagIds = () => Promise<readonly string[]>;

function getErrorMessage(error: unknown): string {
  const i18n = HOOKS.useLocale.getState();

  if (!axios.isAxiosError(error)) {
    return i18n.t('temp.common.error.satellite_certification_generation_failed');
  }

  const responseData = error.response?.data;
  if (typeof responseData === 'string' && responseData.trim()) return responseData;
  if (!responseData || typeof responseData !== 'object') {
    return i18n.t('temp.common.error.satellite_certification_generation_failed');
  }

  const message = (responseData as { message?: unknown }).message;
  if (typeof message === 'string' && message.trim()) return message;

  if (message && typeof message === 'object') {
    const nestedMessage = (message as { message?: unknown }).message;
    if (typeof nestedMessage === 'string' && nestedMessage.trim()) return nestedMessage;
  }

  return i18n.t('temp.common.error.satellite_certification_generation_failed');
}

export function useSatelliteCertification() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const i18n = HOOKS.useLocale.getState();

  const generate = async (resolveSilobagIds: ResolveSilobagIds): Promise<void> => {
    if (isGenerating) return;

    setIsGenerating(true);
    const loadingToastId = toast.loading(
      `${i18n.t('temp.common.toasts.loading.preparing_satellite_certification')} ...`
    );

    try {
      const resolvedIds = await resolveSilobagIds();
      const silobagIds = [
        ...new Set(
          resolvedIds
            .map(String)
            .map((id) => id.trim())
            .filter(Boolean)
        ),
      ];

      if (silobagIds.length === 0) {
        throw new Error(i18n.t('temp.common.error.no_silospro_for_satellite_certification'));
      }

      toast.loading(
        `${i18n.t('temp.common.toasts.loading.generating_satellite_certification_part_1')} ${silobagIds.length} ${i18n.t('temp.common.toasts.loading.generating_satellite_certification_part_1')}`,
        {
          id: loadingToastId,
        }
      );

      await generateSatelliteCertificationService({ silobagIds });

      toast.success(i18n.t('temp.common.toasts.success.satellite_certification_generation_started'), {
        id: loadingToastId,
      });
      setIsModalOpen(true);
    } catch (error) {
      const message =
        error instanceof Error && !axios.isAxiosError(error) ? error.message : getErrorMessage(error);
      toast.error(message, { id: loadingToastId });
    } finally {
      setIsGenerating(false);
    }
  };

  return {
    isGenerating,
    isModalOpen,
    generate,
    closeModal: () => setIsModalOpen(false),
  };
}
